import { createSlice } from '@reduxjs/toolkit'
import { ELittleBotTabs, EManualActions, ETradeOptionStatus, IBotsState, ITradeOptionWithScroll, IWithdrawDraft, IWithdrawSchema } from './types'
import { toast } from 'react-toastify'
import * as actions from './actions'
import { ETransferDirection } from '../rtkApi/subAccountApi'

export const initialState: IBotsState = {
  name: '',
  tabs: {
    tab: ELittleBotTabs.TradeOptions,
  },
  deletingTradeOption: {
    state: false,
    tradeOption: undefined,
  },
  withdrawDataFromBalancesTab: {
    from: 'Binance',
    symbol: 'BTC',
    to: 'Huobi',
  },

  isTradeOptionsHistoryPaused: false,
  tradeOptionsHistory: [],

  totalBalances: [],
  selectedToBalances: [],
  depths: [],
  ordersHistory: {
    totalPages: 0,
    orders: [],
  },
  placeOrderLoading: false,
  placeOrderError: '',
  withdrawLoading: false,
  withdrawError: '',
  depthsLoading: false,

  dexFeeMarketExists: {
    loading: false,
    exists: false,
  },
  firstActionsStatuses: {
    new: 0,
    done: 0,
    error: 0,
  },
  secondActionsStatuses: {
    new: 0,
    done: 0,
    error: 0,
  },
  settingsTab: {
    exchangesList: {
      loading: false,
      data: [],
    },
    exchangeMarketsList: {
      loading: false,
      data: [],
      pages: 1,
    },
    botsListSettings: [],
    marketsBotsListSettings: { markets: [], pages: 1 },
    marketsBotsListSettingsLoading: false,
  },

  tradeOptionsDepthsOptions: {
    loading: false,
    firstEx: {
      symbolFullName: 'N/A',
      pricePrecision: 0,
      volumePrecision: 0,
      volume24: 0,
      change24: 'N/A',
      isActualMarket: true,
    },
    secondEx: {
      symbolFullName: 'N/A',
      pricePrecision: 0,
      volumePrecision: 0,
      volume24: 0,
      change24: 'N/A',
      isActualMarket: true,
    },
  },
  infoPopup: {
    isShow: false,
    autoClose: false,
    status: {
      success: 'Success',
      error: 'Something wrong',
      warning: 'Be careful',
    },
  },
  inputsData: {
    withdrawForm: {
      chains: {
        firstExLoading: false,
        secondExLoading: false,
        firstEx: {
          chainNames: [],
          chains: [],
        },
        secondEx: {
          chainNames: [],
          chains: [],
        },
      },
      verifiedLoading: false,
      verified: {
        verifiedNames: [],
        verified: [],
      },
      isDraftOpened: false,
      draft: undefined,
    },
  },
  token: undefined,

  lowBalances: [],
  isEnabledActive: false,
  selectedTradeOption: {} as ITradeOptionWithScroll,
  placeOrderManualValues: {},
  transferManualValues: {
    manualExchange: 'Binance',
    manualSymbol: 'TRX',
    manualAccountName: '',
    manualDirection: '',
  },
  manualActionsTab: EManualActions.order,
  formMenuOpened: false,
  trades: {
    firstExTrades: [],
    secondExTrades: [],
  },
  actionsFilterByExchange: {
    firstEx: undefined,
    secondEx: undefined,
    market: undefined,
    secondMarket: undefined,
  },
  tradeFirstExType: 'public',
  tradeSecondExType: 'public',
  toFiltersByExchange: undefined,
  toFiltersByMarket: undefined,
  toFiltersBySymbol: undefined,
  toFiltersByStatus: ETradeOptionStatus.all,
  searchTabState: {
    page: 1,
  },
  openedTradeOption: false,
  searchSortState: [] as string[],
  isActionOpen: false,
  recordsTabOpened: false,
  selectedTimelineDot: 0,
  isHideRecorderTOList: false,
}
export const calcReducer = createSlice({
  name: 'calcState',
  initialState,
  reducers: {},
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(actions.togglePauseTradeOptionsHistory, (state, { payload }) => {
      state.isTradeOptionsHistoryPaused = payload
    })
    addCase(actions.editActionsFilterByExchange, (state, { payload }) => {
      if (payload.firstEx && state.actionsFilterByExchange) {
        state.actionsFilterByExchange.firstEx = payload.firstEx === 'All' ? undefined : payload.firstEx
      }
      if (payload.secondEx && state.actionsFilterByExchange) {
        state.actionsFilterByExchange.secondEx = payload.secondEx === 'All' ? undefined : payload.secondEx
      }
      if (payload.market && state.actionsFilterByExchange) {
        state.actionsFilterByExchange.market = payload.market === 'All' ? undefined : payload.market
      }
      if (payload.secondMarket && state.actionsFilterByExchange) {
        state.actionsFilterByExchange.secondMarket = payload.secondMarket === 'All' ? undefined : payload.secondMarket
      }
    })
    addCase(actions.setWithdrawDataFromBalances, (state, { payload }) => {
      state.withdrawDataFromBalancesTab.from = payload.fromEx
      state.withdrawDataFromBalancesTab.to = payload.toEx
      state.withdrawDataFromBalancesTab.symbol = payload.symbol
    })

    addCase(actions.setToken, (state, { payload }) => {
      if (payload) {
        state.token = payload.code
        state.name = payload.name
      }
    })
    addCase(actions.openConfirmDeleting, (state, { payload }) => {
      state.deletingTradeOption.state = payload.state
      state.deletingTradeOption.tradeOption = payload.tradeOption
    })
    addCase(actions.chooseBotSubTab, (state, { payload }) => {
      state.tabs.tab = payload.tab
    })
    addCase(actions.toggleDraftOpened, (state, { payload }) => {
      state.inputsData.withdrawForm.isDraftOpened = payload
    })
    addCase(actions.clearDraft, state => {
      state.inputsData.withdrawForm.draft = undefined
    })
    addCase(actions.setDexFeMarketExistsManually, (state, { payload }) => {
      state.dexFeeMarketExists.exists = payload.exists
    })
    addCase(actions.getInfoWindow, (state, { payload }) => {
      state.infoPopup.isShow = true
      const { type, message } = payload
      switch (type) {
        case 'error': {
          state.infoPopup.status.error = message
          toast.error(state.infoPopup.status.error, { autoClose: false })
          break
        }
        case 'success': {
          state.infoPopup.status.success = message
          toast.success(state.infoPopup.status.success, { autoClose: 3000 })
          break
        }
        case 'warning': {
          state.infoPopup.status.warning = message
          toast.warn(state.infoPopup.status.success, { autoClose: 8000 })
          break
        }
      }
    })

    addCase(actions.clearWithdrawError, state => {
      state.withdrawError = ''
    })

    addCase(actions.clearPlaceOrderError, state => {
      state.placeOrderError = ''
    })

    addCase(actions.setSelectedTradeOption, (state, { payload }) => {
      state.selectedTradeOption = payload
    })
    addCase(actions.setPlaceOrderManualValues, (state, { payload }) => {
      if (payload.withManualTab) {
        state.manualActionsTab = EManualActions.order
      }
      if (payload.manualVolume) {
        state.placeOrderManualValues.manualVolume = payload.manualVolume
      }
      if (payload.manualTotal) {
        state.placeOrderManualValues.manualTotal = payload.manualTotal
      }
      if (payload.manualPrice) {
        state.placeOrderManualValues.manualPrice = payload.manualPrice
      }
    })

    addCase(actions.setManualActionsTab, (state, { payload }) => {
      state.manualActionsTab = payload.tab
    })
    addCase(actions.toggleFormManuOpened, (state, { payload }) => {
      state.formMenuOpened = payload
    })
    addCase(actions.toggleTradeFirstExType, (state, { payload }) => {
      state.tradeFirstExType = payload.type
    })
    addCase(actions.toggleTradeSecondExType, (state, { payload }) => {
      state.tradeSecondExType = payload.type
    })
    addCase(actions.toFiltersByExchange, (state, { payload }) => {
      state.toFiltersByExchange = payload.exchange
    })
    addCase(actions.toFiltersByMarket, (state, { payload }) => {
      state.toFiltersByMarket = payload.market
    })
    addCase(actions.toFiltersBySymbol, (state, { payload }) => {
      state.toFiltersBySymbol = payload.symbol
    })
    addCase(actions.setSearchTabState, (state, { payload }) => {
      if (state.searchTabState) {
        state.searchTabState.symbol = payload.symbol
        state.searchTabState.market = payload.market
        state.searchTabState.exchange = payload.exchange
        state.searchTabState.lowBalance = payload.lowBalance
        state.searchTabState.toStatusGreen = payload.toStatusGreen
        state.searchTabState.toStatusYellow = payload.toStatusYellow
        state.searchTabState.toStatusBlack = payload.toStatusBlack
        state.searchTabState.toFirstEx = payload.toFirstEx
        state.searchTabState.toSecondEx = payload.toSecondEx
        state.searchTabState.toFirstMarket = payload.toFirstMarket
        state.searchTabState.toSecondMarket = payload.toSecondMarket
        state.searchTabState.ping = payload.ping
        state.searchTabState.page = payload.page
        state.searchTabState.sort = payload.sort
      }
    })
    addCase(actions.setOpenedTradeOption, (state, { payload }) => {
      state.openedTradeOption = payload
    })
    addCase(actions.setSearchSortState, (state, { payload }) => {
      state.searchSortState = payload
    })
    addCase(actions.isActionOpen, (state, { payload }) => {
      state.isActionOpen = payload
    })
    addCase(actions.setRecordsTabOpened, (state, { payload }) => {
      state.recordsTabOpened = payload
    })
    addCase(actions.setSelectedTimelineDot, (state, { payload }) => {
      state.selectedTimelineDot = payload
    })
    addCase(actions.setIsHideRecorderTOList, (state, { payload }) => {
      state.isHideRecorderTOList = payload
    })
  },
}).reducer
